import { State } from 'ts-fsrs';
import { buildTodayQueue, isActive } from '../../scheduler/queue.js';
import { descendantIds, liveDecks } from '../../db/repo/decks.js';
import { shuffle } from '../../cardTypes/mcq.js';

export const STUDY_MODES = [
  {
    id: 'today',
    label: 'Today',
    icon: 'play',
    blurb: 'What’s due right now, plus new cards up to each deck’s daily limit.',
    reschedule: true,
  },
  {
    id: 'new',
    label: 'New',
    icon: 'sparkle',
    blurb: 'Cards you haven’t studied yet, past today’s limit if you want.',
    reschedule: true,
  },
  {
    id: 'forgotten',
    label: 'Mistakes',
    icon: 'undo',
    blurb: 'Cards you got wrong recently.',
    days: 'Got wrong in the last',
    reschedule: true,
  },
  {
    id: 'ahead',
    label: 'Ahead',
    icon: 'calendar',
    blurb: 'Reviews that are coming up, so you can get ahead.',
    days: 'Due in the next',
    reschedule: true,
  },
  {
    id: 'flagged',
    label: 'Flagged',
    icon: 'flag',
    blurb: 'Cards you marked to look at again.',
    reschedule: false,
  },
  {
    id: 'tag',
    label: 'Tag',
    icon: 'tag',
    blurb: 'One topic, from whichever deck it’s in.',
    reschedule: false,
  },
  {
    id: 'cram',
    label: 'Mix',
    icon: 'shuffle',
    blurb: 'A random mix of everything. Handy before a test.',
    reschedule: false,
  },
];

const DAY = 86400000;

export function rootDecks(decks) {
  const live = liveDecks(decks);
  const ids = new Set(live.map((d) => d.id));
  return live
    .filter((d) => !d.parentId || !ids.has(d.parentId))
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true }));
}

/** Cards in the chosen top-level decks, including cards inside their subdecks. */
export function cardsInDecks(cards, decks, deckIds) {
  const live = liveDecks(decks);
  const roots = deckIds?.length ? deckIds : rootDecks(decks).map((d) => d.id);
  const allowed = new Set(roots.flatMap((id) => descendantIds(live, id)));
  return cards.filter((c) => !c.deletedAt && allowed.has(c.deckId));
}

function stateOf(card) {
  return card.fsrs?.state ?? State.New;
}

/**
 * What a study setup will actually open.
 * `live` means the normal today queue (limits, learning steps). Otherwise `cardIds` is the list.
 */
export function planStudy({ cards, notes = [], decks, mode, deckIds = [], days = 1, tag = '', limit = 20, now = Date.now(), seed = '0' }) {
  const scope = cardsInDecks(cards, decks, deckIds);
  const active = scope.filter((c) => isActive(c, now));
  const cap = Math.max(1, Number(limit) || 20);
  const modeDef = STUDY_MODES.find((m) => m.id === mode) ?? STUDY_MODES[0];

  if (modeDef.id === 'today') {
    const queue = buildTodayQueue(decks, cards, { now, rootIds: deckIds.length ? deckIds : null });
    const total = queue.counts.new + queue.counts.learn + queue.counts.review;
    return { mode: 'today', counts: queue.counts, total, available: total, cardIds: null, live: true, scopeCount: scope.length };
  }

  let list;
  if (modeDef.id === 'forgotten') {
    list = active
      .filter((c) => c.lastRating === 1 && c.lastReviewedAt >= now - days * DAY)
      .sort((a, b) => (b.lastReviewedAt ?? 0) - (a.lastReviewedAt ?? 0));
  } else if (modeDef.id === 'ahead') {
    list = active
      .filter((c) => stateOf(c) === State.Review && c.due < now + days * DAY)
      .sort((a, b) => a.due - b.due);
  } else if (modeDef.id === 'new') {
    list = active
      .filter((c) => stateOf(c) === State.New)
      .sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0) || a.ord - b.ord);
  } else if (modeDef.id === 'flagged') {
    list = scope.filter((c) => c.flag && !c.suspended);
  } else if (modeDef.id === 'tag') {
    const tagged = new Set(notes.filter((n) => !n.deletedAt && (n.tags ?? []).includes(tag)).map((n) => n.id));
    list = shuffle(
      active.filter((c) => tagged.has(c.noteId)),
      seed,
    );
  } else {
    list = shuffle(active, seed);
  }

  const sliced = list.slice(0, cap);
  return {
    mode: modeDef.id,
    counts: null,
    total: sliced.length,
    available: list.length,
    cardIds: sliced.map((c) => c.id),
    live: false,
    scopeCount: scope.length,
  };
}

export function sessionTitle(mode, deckIds, decks, tag) {
  const label = STUDY_MODES.find((m) => m.id === mode)?.label ?? 'Study';
  const roots = rootDecks(decks);
  const names = (deckIds.length ? deckIds : []).map((id) => roots.find((d) => d.id === id)?.name).filter(Boolean);
  const where = !deckIds.length || names.length === roots.length ? 'All decks' : names.length <= 2 ? names.join(', ') : `${names.length} decks`;
  if (mode === 'tag' && tag) return `${tag} · ${where}`;
  if (mode === 'today') return where === 'All decks' ? 'Today' : `Today · ${where}`;
  return `${label} · ${where}`;
}
